"use client";

import * as React from "react";
import { cn } from "@workspace/ui/lib/utils";
import { StoryPlayIcon } from "./story-icons";

// Figma's glass pill: a 4% white fill over a 12px backdrop blur, and a 0.5px inside stroke that is
// white at the left and right ends (along the top-left and bottom-right) and clear in between.
// The stroke is an SVG rect: Chrome rounds 0.5px CSS borders to 1px. Its gradient uses
// bounding-box units, so like Figma's it is laid out in the 0-1 box and stretched to 244x40.

type StoryFilmButtonProps = {
  id: string;
  label: string;
  onClick: () => void;
  className?: string;
};

/** "Watch Spectral Mapping film" pill: on the section header on mobile, on each card on desktop. */
export function StoryFilmButton({ id, label, onClick, className }: StoryFilmButtonProps) {
  // useId has colons, which break url(#...) references.
  const rimId = `rim${React.useId().replace(/[^a-zA-Z0-9]/g, "")}`;

  return (
    <button
      type="button"
      id={id}
      data-testid={id}
      onClick={onClick}
      className={cn(
        "title-14 relative h-10 w-[244px] shrink-0 cursor-pointer items-center justify-center gap-[9px] rounded-full bg-white/4 px-3.5 py-2.5 text-white backdrop-blur-[12px] transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
        className,
      )}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 244 40"
        fill="none"
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        <defs>
          <linearGradient id={rimId} x2="1.134" y2="0.3">
            <stop offset="0" stopColor="#fff" />
            <stop offset="0.272" stopColor="#fff" stopOpacity="0" />
            <stop offset="0.728" stopColor="#fff" stopOpacity="0" />
            <stop offset="1" stopColor="#fff" />
          </linearGradient>
        </defs>
        <rect
          x="0.25"
          y="0.25"
          width="243.5"
          height="39.5"
          rx="19.75"
          stroke={`url(#${rimId})`}
          strokeWidth="0.5"
          strokeOpacity="0.85"
        />
      </svg>
      <StoryPlayIcon className="h-[15.25px] w-[12.2px] shrink-0" />
      {label}
    </button>
  );
}
